"use client"
import classNames from "classnames";

import { useRouter, useSearchParams } from "next/navigation";
import type { Dispatch } from 'react';
import { useEffect, useReducer } from 'react';
import Ad from 'src/components/ads/Ad';
import withWatchlist from "src/components/filmwebid/withWatchlist";
import StreamingTitleWithProviders from "src/components/streaming/streamingtitlewithproviders/StreamingTitleWithProviders";
import Filter from 'src/components/streaming/weeklystreamingreleases/Filter';
import FilterButton from "src/components/streaming/weeklystreamingreleases/FilterButton";
import type { SupportsScrollRestoration } from "src/lib/client/withScrollRestoration";
import withScrollRestoration from "src/lib/client/withScrollRestoration";
import type { StreamingContent, StreamingProvider } from 'src/lib/movieinfo/movieinfotypes';
import type { ActionType, IStreamingGenre } from "src/lib/streaming/StreamingReleasesFilter";
import { filterReducer, INTIAL_STATE, matchesFilter, STREAMING_GENRES } from "src/lib/streaming/StreamingReleasesFilter";
import styles from "./ReleaseList.module.css";

//#region [Props]
type ReleaseListProps = {
    popular: StreamingContent[];
    streamingProviders: StreamingProvider[];
    className?: string;
} & SupportsScrollRestoration;
//#endregion

//#region [Component]
function ReleaseList({ popular, streamingProviders, className, isReadyForScrollRestoration }: ReleaseListProps) {
    const [filterState, filterDispatch] = useReducer(filterReducer, INTIAL_STATE);
    const filteredReleases = prepareResultData(popular.filter(release => matchesFilter(release, filterState)));
    const router = useRouter();
    const searchParams = useSearchParams();

    useEffect(() => {
        // init state based on url params
        parseInitialUrlState(searchParams, streamingProviders, filterDispatch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        isReadyForScrollRestoration?.(true);
    }, [filterState.selectedGenres, filterState.selectedProviders, popular, isReadyForScrollRestoration]);

    useEffect(() => {
        // Update the url on a filter change
        const newUrl: string[] = [];
        if (filterState.selectedGenres.length > 0) {
            newUrl.push(`genres=${filterState.selectedGenres.map(g => (g.shortName ?? g.name.substring(0, 3))).join("").toLowerCase()}`);
        }
        if (filterState.selectedProviders.length > 0) {
            newUrl.push(`providers=${filterState.selectedProviders.map(p => p.name!.substring(0, 4)).join("").toLowerCase().replace(URL_REPLACE_REGEXP, "_")}`);
        }

        if (newUrl.length > 0) {
            console.debug("Updating url status", newUrl);
            router.replace(`?${newUrl.join("&")}`, { scroll: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState]);

    const isFiltered = (filterState.selectedGenres.length > 0 || filterState.selectedProviders.length > 0);
    const filterButtonText = isFiltered ? `Filter Valgt` : undefined;



    return <div className={classNames(styles.ReleaseList, className)}>
        <header className={styles.SHeader}>
            <FilterButton className={styles.SFilterButton} showFilterDialog={() => filterDispatch({ type: "show" })} text={filterButtonText} />
            <h2 className={styles.SH2}>Populært på streaming</h2>
        </header>
        <div className={styles.SSpaceContainer}>

            {(filteredReleases?.top?.length ?? 0) === 0 && <p>Ingen titler matcher ditt filter</p>}
            {(filteredReleases?.top?.length ?? 0) > 0 && <>
                <div className={styles.SLayout}>
                    {filteredReleases!.top!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
                </div>
                <Ad className={styles.SAd} targetId="SG3_top_pop" height="300" />

                {(filteredReleases?.mid1?.length ?? 0) > 0 && <div className={styles.SLayout}>
                    {filteredReleases!.mid1!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
                </div>}

                {(filteredReleases?.mid2?.length ?? 0) > 0 && <>
                    <Ad className={styles.SAd} targetId="SG3_midt1_pop" height="300" />
                    <div className={styles.SLayout}>
                        {filteredReleases!.mid2!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
                    </div>
                </>}

                {(filteredReleases?.mid3?.length ?? 0) > 0 && <>
                    <Ad className={styles.SAd} targetId="SG3_midt2_pop" height="300" />
                    <div className={styles.SLayout}>
                        {filteredReleases!.mid3!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
                    </div>
                </>}

                {(filteredReleases?.mid4?.length ?? 0) > 0 && <>
                    <Ad className={styles.SAd} targetId="SG3_midt3_pop" height="300" />
                    <div className={styles.SLayout}>
                        {filteredReleases!.mid4!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
                    </div>
                </>}

                {(filteredReleases?.rest?.length ?? 0) > 0 && <>
                    <Ad className={styles.SAd} targetId="SG3_midt4_pop" height="300" />
                    <div className={styles.SLayout}>

                        {filteredReleases!.rest!.map(content => <WLStreamingTitleWithProviders key={content.id} data={content} />)}
                    </div>
                </>}
            </>}

        </div>
        {filterState.showFilter && <Filter streamingProviders={streamingProviders} filterDispatch={filterDispatch} filterState={filterState} />}
    </div>;
}

export default withScrollRestoration(ReleaseList);
//#endregion

const WLStreamingTitleWithProviders = withWatchlist(StreamingTitleWithProviders, "data");
//#region [Other] Helpers
interface IPreparedData {
    top: StreamingContent[];
    mid1: StreamingContent[];
    mid2: StreamingContent[];
    mid3: StreamingContent[];
    mid4: StreamingContent[];
    rest: StreamingContent[];
}

function prepareResultData(streamingData: StreamingContent[] | undefined | null): IPreparedData | undefined {
    if ((streamingData?.length ?? 0) === 0) return undefined;
    return {
        top: streamingData!.slice(0, 4),
        mid1: streamingData!.slice(4, 12),
        mid2: streamingData!.slice(12, 20),
        mid3: streamingData!.slice(20, 28),
        mid4: streamingData!.slice(28, 36),
        rest: streamingData!.slice(36)
    };
}



const URL_REPLACE_REGEXP = /[-._~:/?#[\]@!$&'()*+,;= ]/g;

function parseInitialUrlState(searchParams: ReturnType<typeof useSearchParams>, providers: StreamingProvider[], filterDispatch: Dispatch<ActionType>) {
    // read state from url

    if (searchParams && searchParams.size > 0) {
        let doInitFilter = false;
        let genres: IStreamingGenre[] = [];
        let _providers: StreamingProvider[] = [];
        if (searchParams.has("genres")) {
            const urlGenres = (searchParams.get("genres")!.toLowerCase().match(/.{1,3}/g) ?? []) as string[];
            genres = STREAMING_GENRES.filter(sg => urlGenres.includes((sg.shortName ?? sg.name.substring(0, 3)).toLowerCase()));

            doInitFilter = doInitFilter || genres.length > 0;
            console.debug(urlGenres, genres, doInitFilter);
        }

        if (searchParams.has("providers")) {
            const urlProviders = (searchParams.get("providers")!.toLowerCase().match(/.{1,4}/g) ?? []) as string[];
            _providers = providers.filter(p => urlProviders.includes(p.name!.substring(0, 4).toLowerCase().replace(URL_REPLACE_REGEXP, "_")))
            doInitFilter = doInitFilter || _providers.length > 0;
        }



        if (doInitFilter) {
            console.debug("Updating filter state", genres, _providers);
            filterDispatch({
                type: "preinitfilter",
                genres,
                providers: _providers
            });
        }
        console.groupEnd();
    }
}
//#endregion